import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { toast } from 'sonner';
import {
  Ticket, Plus, Edit, Trash2, ArrowLeft, MousePointer, Clock, Calendar, Palette, ChevronDown, ChevronUp, Image, Upload, RefreshCw, PackageX, PackageCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import EmojiPickerButton from '../../components/EmojiPickerButton';
import UpcomingCouponsAdmin from '../../components/UpcomingCouponsAdmin';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import ImageUploader from '../../components/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCupones = () => {
  const { token } = useAuth();
  const { settings } = useSite();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [fetchingImages, setFetchingImages] = useState(false);
  const [bulkExpType, setBulkExpType] = useState('days'); // 'days' | 'date'
  const [bulkExpDays, setBulkExpDays] = useState(365);
  const [bulkExpDate, setBulkExpDate] = useState('');
  const [bulkCouponOpen, setBulkCouponOpen] = useState(false);
  const [bulkCouponText, setBulkCouponText] = useState('');
  const [bulkCouponDate, setBulkCouponDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkCouponType, setBulkCouponType] = useState('store');
  const [bulkCouponImporting, setBulkCouponImporting] = useState(false);
  const [couponPreview, setCouponPreview] = useState([]);
  const [expirationType, setExpirationType] = useState('date');
  const [showCustomColors, setShowCustomColors] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    code: '',
    min_purchase: '',
    max_discount: '',
    stock: '',
    expiration_date: '',
    expiration_hours: 1,
    expiration_minutes: 0,
    is_active: true,
    coupon_type: 'store',
    item_kind: 'coupon',
    product_link: '',
    price: '',
    offer_price: '',
    offer_label: '',
    offer_categories: [],
    search_only: false,
    image_url: '',
    // Custom colors
    custom_bg_color: '',
    custom_discount_bg: '',
    custom_button_bg: '',
    custom_button_text: '',
    custom_text_color: '',
    custom_min_purchase_bg: '',
    custom_tag_color: '',
    custom_border_color: ''
  });
  const [saving, setSaving] = useState(false);
  const [adminFilter, setAdminFilter] = useState('all');

  useEffect(() => {
    fetchCoupons();
  }, [token]);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Error al cargar los cupones');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    // Parse lines: "Nombre | Link | Precio"
    const lines = bulkText.split('\n').map((l) => l.trim()).filter(Boolean);
    const items = [];
    let invalid = 0;
    for (const line of lines) {
      const parts = line.split('|').map((p) => p.trim());
      const title = parts[0] || '';
      const link = parts[1] || '';
      const priceRaw = (parts[2] || '').replace(/[^0-9.]/g, '');
      const price = priceRaw ? parseFloat(priceRaw) : null;
      const image_url = parts[3] || '';
      if (!title || !link) { invalid++; continue; }
      items.push({ title, product_link: link, price, image_url });
    }
    if (items.length === 0) {
      toast.error('No se encontraron ofertas válidas. Usa: Nombre | Link | Precio | Imagen(opcional)');
      return;
    }
    setBulkImporting(true);
    try {
      const payload = { items };
      if (bulkExpType === 'date') {
        if (!bulkExpDate) {
          toast.error('Elige una fecha de caducidad o cambia a "en X días".');
          setBulkImporting(false);
          return;
        }
        payload.expiration_date = `${bulkExpDate}T23:59:59`;
      } else {
        payload.expiration_days = parseInt(bulkExpDays) || 365;
      }
      const res = await axios.post(`${API}/admin/coupons/bulk-offers`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.created} ofertas importadas${res.data.replaced ? ` · ${res.data.replaced} reemplazadas` : ''}${invalid ? ` · ${invalid} líneas ignoradas` : ''}`);
      setBulkText('');
      setBulkOpen(false);
      await fetchCoupons();
      // Auto-fill images from product links (og:image), best-effort in background
      handleFetchImages(true);
    } catch (error) {
      console.error('Bulk import error:', error);
      toast.error('Error al importar las ofertas');
    } finally {
      setBulkImporting(false);
    }
  };

  const handleClearImported = async () => {
    if (!window.confirm('¿Borrar TODAS las ofertas importadas (solo búsqueda)? Esto no afecta las ofertas manuales ni los cupones.')) return;
    try {
      const res = await axios.post(`${API}/admin/coupons/clear-imported`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.deleted} ofertas importadas eliminadas`);
      await fetchCoupons();
    } catch (error) {
      console.error('Clear imported error:', error);
      toast.error('Error al vaciar las ofertas importadas');
    }
  };

  const handleFetchImages = async (silent = false) => {
    setFetchingImages(true);
    let totalUpdated = 0;
    try {
      // Loop in batches (backend processes ~60 per call) until nothing remains
      for (let i = 0; i < 20; i++) {
        const res = await axios.post(`${API}/admin/offers/fetch-images`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        totalUpdated += res.data.updated || 0;
        if (!silent) toast.info(`Descargando imágenes... ${totalUpdated} listas`);
        if (!res.data.remaining || res.data.checked === 0) break;
      }
      if (totalUpdated > 0) {
        toast.success(`${totalUpdated} imágenes obtenidas de los links 🎉`);
        await fetchCoupons();
      } else if (!silent) {
        toast.info('No había imágenes por descargar (o los links no la tienen disponible).');
      }
    } catch (error) {
      console.error('Fetch images error:', error);
      if (!silent) toast.error('Error al obtener las imágenes.');
    } finally {
      setFetchingImages(false);
    }
  };

  const parseCouponsText = (text) => {
    const lines = text.split('\n');
    const items = [];
    let cur = null;
    const push = () => { if (cur && cur.code) items.push(cur); };
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;
      const lower = line.toLowerCase();
      let idx = lower.indexOf('cupón:');
      let markerLen = 'cupón:'.length;
      if (idx < 0) { idx = lower.indexOf('cupon:'); markerLen = 'cupon:'.length; }
      if (idx >= 0) {
        push();
        cur = { code: line.slice(idx + markerLen).trim(), title: '', min_purchase: null, discountLine: '' };
        continue;
      }
      if (!cur) continue;
      if (lower.includes('compra m')) {
        cur.min_purchase = parseInt(line.replace(/[^0-9]/g, ''), 10) || null;
      } else if (lower.includes('descuento m')) {
        const n = parseInt(line.replace(/[^0-9]/g, ''), 10);
        if (n) cur.title = '$' + n.toLocaleString('en-US');
      } else if (lower.includes('de descuento')) {
        cur.discountLine = line;
      }
    }
    push();
    return items.map((it) => ({
      code: it.code,
      title: it.title || (it.discountLine ? it.discountLine.replace(/de descuento.*/i, '').trim() : it.code),
      min_purchase: it.min_purchase,
    }));
  };

  const handleBulkCoupons = async () => {
    const items = couponPreview
      .map((c) => ({ code: (c.code || '').trim(), title: (c.title || '').trim(), min_purchase: c.min_purchase }))
      .filter((c) => c.code);
    if (items.length === 0) {
      toast.error('No se encontraron cupones válidos. Verifica el formato.');
      return;
    }
    const exp = bulkCouponDate
      ? new Date(`${bulkCouponDate}T23:59:59`).toISOString()
      : undefined;
    setBulkCouponImporting(true);
    try {
      const res = await axios.post(`${API}/admin/coupons/bulk-coupons`, { items, expiration_date: exp, coupon_type: bulkCouponType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.created} cupones importados`);
      setBulkCouponText('');
      setCouponPreview([]);
      setBulkCouponOpen(false);
      await fetchCoupons();
    } catch (error) {
      console.error('Bulk coupons error:', error);
      toast.error('Error al importar los cupones');
    } finally {
      setBulkCouponImporting(false);
    }
  };

  const handleOpenDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setExpirationType('date');
      setShowCustomColors(!!coupon.custom_bg_color || !!coupon.custom_discount_bg);
      setFormData({
        title: coupon.title,
        description: coupon.description || '',
        code: coupon.code,
        min_purchase: coupon.min_purchase || '',
        max_discount: coupon.max_discount || '',
        stock: coupon.stock ?? '',
        affiliate_link: coupon.affiliate_link || '',
        expiration_date: coupon.expiration_date ? coupon.expiration_date.split('T')[0] : '',
        expiration_hours: 1,
        expiration_minutes: 0,
        is_active: coupon.is_active,
        coupon_type: coupon.coupon_type || 'store',
        item_kind: coupon.item_kind || 'coupon',
        product_link: coupon.product_link || '',
        price: coupon.price || '',
        offer_price: coupon.offer_price || '',
        offer_label: coupon.offer_label || '',
        offer_categories: Array.isArray(coupon.offer_categories) ? coupon.offer_categories : [],
        search_only: !!coupon.search_only,
        image_url: coupon.image_url || '',
        custom_bg_color: coupon.custom_bg_color || '',
        custom_discount_bg: coupon.custom_discount_bg || '',
        custom_button_bg: coupon.custom_button_bg || '',
        custom_button_text: coupon.custom_button_text || '',
        custom_text_color: coupon.custom_text_color || '',
        custom_min_purchase_bg: coupon.custom_min_purchase_bg || '',
        custom_tag_color: coupon.custom_tag_color || '',
        custom_border_color: coupon.custom_border_color || ''
      });
    } else {
      setEditingCoupon(null);
      setExpirationType('date');
      setShowCustomColors(false);
      setFormData({
        title: '',
        description: '',
        code: '',
        min_purchase: '',
        max_discount: '',
        stock: '',
        affiliate_link: '',
        expiration_date: '',
        expiration_hours: 1,
        expiration_minutes: 0,
        is_active: true,
        coupon_type: 'store',
        item_kind: 'coupon',
        product_link: '',
        price: '',
        offer_price: '',
        offer_label: '',
        offer_categories: [],
        search_only: false,
        image_url: '',
        custom_bg_color: '',
        custom_discount_bg: '',
        custom_button_bg: '',
        custom_button_text: '',
        custom_text_color: '',
        custom_min_purchase_bg: '',
        custom_tag_color: '',
        custom_border_color: ''
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    const isProduct = formData.item_kind === 'product';
    if (!formData.title) {
      toast.error('Por favor completa el título');
      return;
    }
    if (!isProduct && !formData.code) {
      toast.error('Por favor completa el código del cupón');
      return;
    }
    if (isProduct && !formData.product_link) {
      toast.error('Por favor agrega el enlace del producto');
      return;
    }

    if (expirationType === 'date' && !formData.expiration_date) {
      toast.error('Por favor selecciona una fecha de expiración');
      return;
    }

    if (expirationType === 'time' && formData.expiration_hours === 0 && formData.expiration_minutes === 0) {
      toast.error('Por favor ingresa un tiempo de expiración');
      return;
    }

    setSaving(true);
    try {
      let expirationDate;
      if (expirationType === 'date') {
        expirationDate = new Date(formData.expiration_date + 'T23:59:59').toISOString();
      } else {
        const now = new Date();
        now.setHours(now.getHours() + parseInt(formData.expiration_hours || 0));
        now.setMinutes(now.getMinutes() + parseInt(formData.expiration_minutes || 0));
        expirationDate = now.toISOString();
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        code: formData.code || '',
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : null,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        stock: (formData.stock === '' || formData.stock === null || formData.stock === undefined) ? null : parseInt(formData.stock),
        affiliate_link: formData.affiliate_link || null,
        expiration_date: expirationDate,
        is_active: formData.is_active,
        coupon_type: formData.coupon_type,
        item_kind: formData.item_kind || 'coupon',
        product_link: formData.product_link || null,
        price: formData.price ? parseFloat(formData.price) : null,
        offer_price: formData.offer_price ? parseFloat(formData.offer_price) : null,
        offer_label: formData.offer_label || null,
        offer_categories: formData.item_kind === 'product' ? (formData.offer_categories || []) : null,
        search_only: formData.item_kind === 'product' ? !!formData.search_only : false,
        image_url: formData.image_url || null,
        // Custom colors (only if set)
        custom_bg_color: formData.custom_bg_color || null,
        custom_discount_bg: formData.custom_discount_bg || null,
        custom_button_bg: formData.custom_button_bg || null,
        custom_button_text: formData.custom_button_text || null,
        custom_text_color: formData.custom_text_color || null,
        custom_min_purchase_bg: formData.custom_min_purchase_bg || null,
        custom_tag_color: formData.custom_tag_color || null,
        custom_border_color: formData.custom_border_color || null
      };

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cupón actualizado correctamente');
      } else {
        await axios.post(`${API}/coupons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cupón creado correctamente');
      }

      setShowDialog(false);
      await fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el cupón');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cupón?')) return;

    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cupón eliminado');
      await fetchCoupons();
    } catch (error) {
      toast.error('Error al eliminar el cupón');
    }
  };

  const isSoldOut = (coupon) => typeof coupon.stock === 'number' && coupon.stock <= 0;

  const handleToggleSoldOut = async (coupon) => {
    try {
      const res = await axios.post(`${API}/admin/coupons/${coupon.id}/toggle-soldout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.sold_out ? 'Cupón marcado como agotado' : 'Cupón disponible de nuevo');
      await fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el cupón');
    }
  };

  const isExpired = (date) => new Date(date) < new Date();
  
  const formatExpiration = (date) => {
    const expDate = new Date(date);
    const now = new Date();
    const diffMs = expDate - now;
    
    if (diffMs < 0) return 'Expirado';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours < 24) {
      return `${diffHours}h ${diffMins}m restantes`;
    }
    
    return expDate.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter coupons based on the active admin tab
  const filteredCoupons = coupons.filter(c => {
    if (adminFilter === 'all') return true;
    if (adminFilter === 'product') return c.item_kind === 'product' && !c.search_only;
    if (adminFilter === 'imported') return c.item_kind === 'product' && !!c.search_only;
    if (adminFilter === 'store') return (c.item_kind || 'coupon') === 'coupon' && (c.coupon_type || 'store') === 'store';
    if (adminFilter === 'bank') return (c.item_kind || 'coupon') === 'coupon' && c.coupon_type === 'bank';
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 pb-20 md:pb-6" data-testid="admin-cupones">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/admin" className="p-2 bg-white rounded-lg shadow-sm hover:bg-slate-50">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="font-fredoka text-xl sm:text-2xl font-bold text-slate-900">Cupones</h1>
            <p className="text-slate-600 text-xs sm:text-base hidden sm:block">Administra tus cupones de descuento</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setBulkCouponOpen(true)}
            variant="outline"
            className="text-sm sm:text-base"
            data-testid="bulk-import-coupons-btn"
          >
            <Upload className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Importar cupones</span>
          </Button>
          <Button
            onClick={() => setBulkOpen(true)}
            variant="outline"
            className="text-sm sm:text-base"
            data-testid="bulk-import-btn"
          >
            <Upload className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Importar ofertas</span>
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-canary text-slate-900 hover:bg-canary-hover text-sm sm:text-base"
            data-testid="add-coupon-btn"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Nuevo Cupón</span>
          </Button>
        </div>
      </div>

      {/* Bulk import COUPONS dialog */}
      <Dialog open={bulkCouponOpen} onOpenChange={setBulkCouponOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar cupones en masa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pega los cupones tal cual te los envían. Se detecta automáticamente:
              el <strong>título</strong> = descuento máximo, el <strong>código</strong> = "Cupón:",
              y la <strong>compra mínima</strong>.
            </p>
            <div className="text-xs bg-slate-100 rounded-lg p-3 font-mono text-slate-700 whitespace-pre-line">
              🎟️Cupón: TENDENCIA24{'\n'}$1.000 de descuento{'\n'}Compra minima: $9.000{'\n'}Descuento máximo: $1.000
            </div>
            <div>
              <Label className="text-xs">Fecha de expiración (por defecto: hoy)</Label>
              <Input
                type="date"
                value={bulkCouponDate}
                onChange={(e) => setBulkCouponDate(e.target.value)}
                data-testid="bulk-coupon-date"
              />
            </div>

            {/* Category / tab selector */}
            <div>
              <Label className="text-xs">Categoría para todos estos cupones</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(() => {
                  const DEFAULT_TABS = [
                    { id: 'store', emoji: '🛒', name: 'Tienda' },
                    { id: 'bank', emoji: '🏦', name: 'Bancarios' }
                  ];
                  let tabs = DEFAULT_TABS;
                  try {
                    const parsed = JSON.parse(settings.coupon_tabs || '[]');
                    if (parsed.length > 0) tabs = parsed;
                  } catch (e) { /* ignore */ }
                  ['store', 'bank'].forEach(id => {
                    if (!tabs.some(t => t.id === id)) {
                      const def = DEFAULT_TABS.find(t => t.id === id);
                      if (def) tabs = [...tabs, def];
                    }
                  });
                  return tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      data-testid={`bulk-coupon-tab-${tab.id}`}
                      onClick={() => setBulkCouponType(tab.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                        bulkCouponType === tab.id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {tab.emoji && <span className="mr-1">{tab.emoji}</span>}{tab.name}
                    </button>
                  ));
                })()}
              </div>
            </div>

            <Textarea
              value={bulkCouponText}
              onChange={(e) => { setBulkCouponText(e.target.value); setCouponPreview(parseCouponsText(e.target.value)); }}
              rows={7}
              placeholder={"🎟️Cupón: TENDENCIA24\n$1.000 de descuento\nCompra minima: $9.000\nDescuento máximo: $1.000"}
              data-testid="bulk-coupon-textarea"
              className="font-mono text-xs"
            />

            {/* Editable preview table with duplicate detection */}
            {(() => {
              if (couponPreview.length === 0) return null;
              const existingCodes = new Set((coupons || [])
                .map((c) => (c.code || '').trim().toLowerCase())
                .filter(Boolean));
              const isDup = (code, idx) => {
                const c = (code || '').trim().toLowerCase();
                if (!c) return false;
                if (existingCodes.has(c)) return true;
                return couponPreview.findIndex((x, i) => i !== idx && (x.code || '').trim().toLowerCase() === c) !== -1;
              };
              const dupCount = couponPreview.filter((c, i) => isDup(c.code, i)).length;
              const update = (idx, field, value) => {
                setCouponPreview(couponPreview.map((c, i) => i === idx ? { ...c, [field]: value } : c));
              };
              const removeRow = (idx) => setCouponPreview(couponPreview.filter((_, i) => i !== idx));
              return (
                <div data-testid="bulk-coupon-preview">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-600">Vista previa · edita antes de importar ({couponPreview.length})</p>
                    {dupCount > 0 && (
                      <span className="text-[11px] font-bold text-red-600" data-testid="bulk-coupon-dup-warning">
                        ⚠️ {dupCount} código{dupCount > 1 ? 's' : ''} ya existe{dupCount > 1 ? 'n' : ''}
                      </span>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-600 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold">Título</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Código</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Compra mín.</th>
                          <th className="px-1 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponPreview.map((c, i) => {
                          const dup = isDup(c.code, i);
                          return (
                            <tr key={i} className={`border-t border-slate-100 ${dup ? 'bg-red-50' : ''}`}>
                              <td className="px-1.5 py-1">
                                <input
                                  value={c.title || ''}
                                  onChange={(e) => update(i, 'title', e.target.value)}
                                  data-testid={`bulk-preview-title-${i}`}
                                  className="w-full px-2 py-1 rounded border border-slate-200 font-bold text-slate-800 bg-white"
                                />
                              </td>
                              <td className="px-1.5 py-1">
                                <input
                                  value={c.code || ''}
                                  onChange={(e) => update(i, 'code', e.target.value)}
                                  data-testid={`bulk-preview-code-${i}`}
                                  className={`w-full px-2 py-1 rounded border font-mono bg-white ${dup ? 'border-red-300 text-red-700' : 'border-slate-200 text-slate-600'}`}
                                />
                                {dup && <span className="text-[10px] text-red-600 font-semibold">ya existe</span>}
                              </td>
                              <td className="px-1.5 py-1">
                                <input
                                  type="number"
                                  value={c.min_purchase ?? ''}
                                  onChange={(e) => update(i, 'min_purchase', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                  data-testid={`bulk-preview-min-${i}`}
                                  className="w-full px-2 py-1 rounded border border-slate-200 text-slate-600 bg-white"
                                />
                              </td>
                              <td className="px-1 py-1 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeRow(i)}
                                  data-testid={`bulk-preview-remove-${i}`}
                                  className="p-1 text-red-400 hover:text-red-600"
                                  title="Quitar"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {dupCount > 0 && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Los cupones marcados en rojo ya existen. Cambia su código o quítalos con la papelera antes de importar.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCouponOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleBulkCoupons}
              disabled={bulkCouponImporting}
              className="bg-canary text-slate-900 hover:bg-canary-hover"
              data-testid="bulk-coupon-submit"
            >
              {bulkCouponImporting ? 'Importando...' : 'Importar cupones'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import offers dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar ofertas en masa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pega una oferta por línea con este formato:
            </p>
            <div className="text-xs bg-slate-100 rounded-lg p-3 font-mono text-slate-700">
              Nombre del producto | https://tu-link-afiliado | precio | urlImagen
            </div>
            <p className="text-xs text-slate-500">
              El precio y la imagen son opcionales. Con imagen se muestra en el buscador. Ejemplo:<br />
              iPhone 17 Pro | https://mercadolibre.com/sec/abc | 21999 | https://http2.mlstatic.com/foto.jpg
            </p>
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              placeholder={"iPhone 17 Pro | https://mercadolibre.com/sec/abc | 21999 | https://.../foto.jpg\nTenis Nike Air | https://mercadolibre.com/sec/xyz | 1499"}
              data-testid="bulk-import-textarea"
              className="font-mono text-xs"
            />

            {/* Expiration control */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-slate-700 mb-2">¿Cuándo caducan estas ofertas?</p>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setBulkExpType('days')}
                  data-testid="bulk-exp-days-tab"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${bulkExpType === 'days' ? 'bg-royal text-white border-royal' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  En X días
                </button>
                <button
                  type="button"
                  onClick={() => setBulkExpType('date')}
                  data-testid="bulk-exp-date-tab"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${bulkExpType === 'date' ? 'bg-royal text-white border-royal' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Fecha exacta
                </button>
              </div>
              {bulkExpType === 'days' ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    value={bulkExpDays}
                    onChange={(e) => setBulkExpDays(e.target.value)}
                    data-testid="bulk-exp-days-input"
                    className="w-28"
                  />
                  <span className="text-sm text-slate-500">días desde hoy</span>
                </div>
              ) : (
                <Input
                  type="date"
                  value={bulkExpDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setBulkExpDate(e.target.value)}
                  data-testid="bulk-exp-date-input"
                  className="w-full"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleBulkImport}
              disabled={bulkImporting}
              className="bg-canary text-slate-900 hover:bg-canary-hover"
              data-testid="bulk-import-submit"
            >
              {bulkImporting ? 'Importando...' : 'Importar ofertas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter tabs */}
      {(() => {
        const filterTabs = [
          { id: 'all', label: 'Todos', emoji: '📋' },
          { id: 'store', label: 'Tienda', emoji: '🛒' },
          { id: 'bank', label: 'Bancarios', emoji: '🏦' },
          { id: 'product', label: 'Ofertas', emoji: '🛍️' },
          { id: 'imported', label: 'Ofertas importadas', emoji: '🔎' },
          { id: 'upcoming', label: 'Próximos', emoji: '⚡' },
        ];
        const counts = {
          all: coupons.length,
          store: coupons.filter(c => (c.item_kind || 'coupon') === 'coupon' && (c.coupon_type || 'store') === 'store').length,
          bank: coupons.filter(c => (c.item_kind || 'coupon') === 'coupon' && c.coupon_type === 'bank').length,
          product: coupons.filter(c => c.item_kind === 'product' && !c.search_only).length,
          imported: coupons.filter(c => c.item_kind === 'product' && !!c.search_only).length,
        };
        return (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide" data-testid="admin-cupones-filter-tabs">
            {filterTabs.map(tab => {
              const active = adminFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setAdminFilter(tab.id)}
                  data-testid={`admin-filter-${tab.id}`}
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all active:scale-95 border ${
                    active
                      ? 'bg-slate-900 border-slate-900 text-white shadow-sm'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="mr-1">{tab.emoji}</span>
                  {tab.label}
                  {counts[tab.id] !== undefined && (
                    <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {counts[tab.id]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Imported offers actions (only on imported tab) */}
      {adminFilter === 'imported' && coupons.filter(c => c.item_kind === 'product' && !!c.search_only).length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2 items-center">
          <Button
            onClick={() => handleFetchImages(false)}
            disabled={fetchingImages}
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50 text-sm"
            data-testid="fetch-images-btn"
          >
            <Image className="w-4 h-4 mr-2" />
            {fetchingImages ? 'Obteniendo imágenes...' : 'Obtener imágenes de los links'}
          </Button>
          {(() => {
            const failed = coupons.filter(c => c.item_kind === 'product' && !!c.search_only && c.product_link && !c.image_url).length;
            return failed > 0 ? (
              <Button
                onClick={() => handleFetchImages(false)}
                disabled={fetchingImages}
                variant="outline"
                className="text-amber-600 border-amber-200 hover:bg-amber-50 text-sm"
                data-testid="retry-images-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${fetchingImages ? 'animate-spin' : ''}`} />
                Reintentar fallidas ({failed})
              </Button>
            ) : (
              <span className="text-xs text-green-600 font-medium inline-flex items-center gap-1" data-testid="all-images-ok">
                <Image className="w-3.5 h-3.5" /> Todas con imagen
              </span>
            );
          })()}
          <Button
            onClick={handleClearImported}
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 text-sm"
            data-testid="clear-imported-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Vaciar todas las importadas ({coupons.filter(c => c.item_kind === 'product' && !!c.search_only).length})
          </Button>
        </div>
      )}

      {/* Próximos cupones admin section */}
      {adminFilter === 'upcoming' && <UpcomingCouponsAdmin API={API} token={token} />}

      {/* Coupons - Mobile Cards / Desktop Table */}
      {adminFilter !== 'upcoming' && (
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-4 sm:p-6 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="w-12 h-12 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : coupons.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay cupones creados</p>
            <Button
              onClick={() => handleOpenDialog()}
              className="mt-4 bg-canary text-slate-900 hover:bg-canary-hover"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primer cupón
            </Button>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay cupones en esta categoría</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card View */}
            <div className="sm:hidden divide-y divide-slate-100">
              {filteredCoupons.map((coupon) => (
                <div key={coupon.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ backgroundColor: coupon.custom_discount_bg || settings.default_coupon_discount_bg || '#4f46e5' }}
                    >
                      {coupon.title.split(' ')[0].replace('%', '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{coupon.title}</p>
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-mono">
                            {coupon.code}
                          </code>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                          isExpired(coupon.expiration_date)
                            ? 'bg-red-100 text-red-700'
                            : isSoldOut(coupon)
                              ? 'bg-amber-100 text-amber-700'
                              : coupon.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-700'
                        }`}>
                          {isExpired(coupon.expiration_date) ? 'Expirado' : isSoldOut(coupon) ? 'Agotado' : coupon.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {coupon.min_purchase && (
                          <span>Mín: ${coupon.min_purchase.toLocaleString()}</span>
                        )}
                        <span className={isExpired(coupon.expiration_date) ? 'text-red-600' : ''}>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatExpiration(coupon.expiration_date)}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1 text-slate-600">
                            <MousePointer className="w-3 h-3" /> {coupon.clicks || 0}
                          </span>
                          <span className="flex items-center gap-1 text-emerald-600">
                            👍 {coupon.worked_count || 0}
                          </span>
                          <span className="flex items-center gap-1 text-red-500">
                            👎 {coupon.not_worked_count || 0}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {(coupon.item_kind || 'coupon') !== 'product' && (
                            <button
                              onClick={() => handleToggleSoldOut(coupon)}
                              data-testid={`coupon-toggle-soldout-mobile-${coupon.id}`}
                              title={isSoldOut(coupon) ? 'Marcar como disponible' : 'Marcar como agotado'}
                              className={`p-2 rounded-lg transition-colors ${isSoldOut(coupon) ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
                            >
                              {isSoldOut(coupon) ? <PackageCheck className="w-4 h-4" /> : <PackageX className="w-4 h-4" />}
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenDialog(coupon)}
                            className="p-2 text-slate-500 hover:text-royal hover:bg-royal/10 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(coupon.id)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Cupón</th>
                    <th className="text-left p-4 font-medium text-slate-600">Código</th>
                    <th className="text-left p-4 font-medium text-slate-600">Expiración</th>
                    <th className="text-left p-4 font-medium text-slate-600">Clicks</th>
                    <th className="text-left p-4 font-medium text-slate-600">Votos</th>
                    <th className="text-left p-4 font-medium text-slate-600">Estado</th>
                    <th className="text-left p-4 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCoupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: coupon.custom_discount_bg || settings.default_coupon_discount_bg || '#4f46e5' }}
                          >
                            {coupon.title.split(' ')[0].replace('%', '')}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{coupon.title}</p>
                            {coupon.min_purchase && (
                              <p className="text-xs text-slate-500">Mín: ${coupon.min_purchase.toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                          {coupon.code}
                        </code>
                      </td>
                      <td className="p-4">
                        <span className={`text-sm ${isExpired(coupon.expiration_date) ? 'text-red-600' : 'text-slate-600'}`}>
                          {formatExpiration(coupon.expiration_date)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="flex items-center gap-1 text-slate-600">
                          <MousePointer className="w-4 h-4" /> {coupon.clicks || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-emerald-600 font-medium" title="Me funcionó">
                            👍 {coupon.worked_count || 0}
                          </span>
                          <span className="flex items-center gap-1 text-red-500 font-medium" title="No me funcionó">
                            👎 {coupon.not_worked_count || 0}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isExpired(coupon.expiration_date)
                            ? 'bg-red-100 text-red-700'
                            : isSoldOut(coupon)
                              ? 'bg-amber-100 text-amber-700'
                              : coupon.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-700'
                        }`}>
                          {isExpired(coupon.expiration_date) ? 'Expirado' : isSoldOut(coupon) ? 'Agotado' : coupon.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {(coupon.item_kind || 'coupon') !== 'product' && (
                            <button
                              onClick={() => handleToggleSoldOut(coupon)}
                              data-testid={`coupon-toggle-soldout-${coupon.id}`}
                              title={isSoldOut(coupon) ? 'Marcar como disponible' : 'Marcar como agotado'}
                              className={`p-2 rounded-lg transition-colors ${isSoldOut(coupon) ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
                            >
                              {isSoldOut(coupon) ? <PackageCheck className="w-4 h-4" /> : <PackageX className="w-4 h-4" />}
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenDialog(coupon)}
                            className="p-2 text-slate-500 hover:text-royal hover:bg-royal/10 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(coupon.id)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">
              {editingCoupon ? 'Editar Cupón' : 'Nuevo Cupón'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Item Kind Selection */}
            <div className="space-y-2">
              <Label>¿Qué quieres publicar?</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="item-kind-coupon"
                  onClick={() => setFormData({ ...formData, item_kind: 'coupon' })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                    formData.item_kind !== 'product'
                      ? 'border-canary bg-canary/10 text-slate-900 font-semibold'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  🎟️ Cupón
                </button>
                <button
                  type="button"
                  data-testid="item-kind-product"
                  onClick={() => setFormData({ ...formData, item_kind: 'product' })}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                    formData.item_kind === 'product'
                      ? 'border-emerald-500 bg-emerald-50 text-slate-900 font-semibold'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  🛍️ Producto / Oferta
                </button>
              </div>
            </div>

            {/* Coupon Type Selection - dynamic tabs */}
            <div className="space-y-2">
              <Label>Pestaña del Cupón</Label>
              <div className="flex gap-2 flex-wrap">
                {(() => {
                  const DEFAULT_TABS = [
                    { id: 'store', emoji: '🛒', name: 'Tienda' },
                    { id: 'bank', emoji: '🏦', name: 'Bancarios' }
                  ];
                  let tabs = DEFAULT_TABS;
                  try {
                    const parsed = JSON.parse(settings.coupon_tabs || '[]');
                    if (parsed.length > 0) tabs = parsed;
                  } catch (e) {
                    console.error('Failed to parse coupon_tabs:', e);
                  }
                  // Ensure both default tabs (store + bank) always exist so admin can create both types
                  ['store', 'bank'].forEach(id => {
                    if (!tabs.some(t => t.id === id)) {
                      const def = DEFAULT_TABS.find(t => t.id === id);
                      if (def) tabs = [...tabs, def];
                    }
                  });
                  const tabIds = tabs.map(t => t.id);
                  const effectiveType = tabIds.includes(formData.coupon_type) ? formData.coupon_type : tabs[0].id;
                  return tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      data-testid={`coupon-form-tab-${tab.id}`}
                      onClick={() => setFormData({ ...formData, coupon_type: tab.id })}
                      className={`flex-1 min-w-[110px] py-3 px-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                        effectiveType === tab.id
                          ? 'border-canary bg-canary/10 text-slate-900 font-semibold'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {tab.emoji && <span>{tab.emoji}</span>}
                      {tab.name}
                    </button>
                  ));
                })()}
              </div>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>{formData.item_kind === 'product' ? 'Imagen del Producto' : formData.coupon_type === 'bank' ? 'Logo del Banco' : 'Imagen del Cupón (opcional)'}</Label>
              <ImageUploader
                currentImage={formData.image_url}
                onImageChange={(url) => setFormData({ ...formData, image_url: url })}
                label={formData.item_kind === 'product' ? 'Subir imagen del producto' : formData.coupon_type === 'bank' ? 'Subir logo del banco' : 'Subir imagen'}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{formData.item_kind === 'product' ? 'Título del Producto *' : 'Título del Cupón *'}</Label>
                <EmojiPickerButton onSelect={(emoji) => setFormData((prev) => ({ ...prev, title: (prev.title || '') + emoji }))} />
              </div>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={formData.item_kind === 'product' ? 'Ej: Audífonos Bluetooth Sony' : 'Ej: 20% OFF Electrónica'}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Descripción</Label>
                <EmojiPickerButton onSelect={(emoji) => setFormData((prev) => ({ ...prev, description: (prev.description || '') + emoji }))} />
              </div>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={formData.item_kind === 'product' ? 'Descripción del producto...' : 'Descripción del cupón...'}
                rows={2}
              />
            </div>

            {formData.item_kind === 'product' ? (
              <>
                <div className="space-y-2">
                  <Label>Enlace del Producto *</Label>
                  <Input
                    data-testid="product-link-input"
                    value={formData.product_link}
                    onChange={(e) => setFormData({ ...formData, product_link: e.target.value })}
                    placeholder="https://mercadolibre.com.mx/producto..."
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Precio Original</Label>
                    <Input
                      data-testid="product-price-input"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="999"
                      min="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio de Oferta</Label>
                    <Input
                      data-testid="product-offer-price-input"
                      type="number"
                      value={formData.offer_price}
                      onChange={(e) => setFormData({ ...formData, offer_price: e.target.value })}
                      placeholder="699"
                      min="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Etiqueta (opcional)</Label>
                    <Input
                      data-testid="product-offer-label-input"
                      value={formData.offer_label}
                      onChange={(e) => setFormData({ ...formData, offer_label: e.target.value })}
                      placeholder="-30% / OFERTA"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Si dejas la etiqueta vacía, se calcula el % de descuento automáticamente con los precios.</p>

                {/* Search-only toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="pr-3">
                    <Label className="text-sm">Solo en búsqueda</Label>
                    <p className="text-[11px] text-slate-500">Si se activa, la oferta NO aparece en la lista de Ofertas; solo se muestra cuando el usuario la busca.</p>
                  </div>
                  <Switch
                    checked={!!formData.search_only}
                    onCheckedChange={(v) => setFormData({ ...formData, search_only: v })}
                    data-testid="offer-search-only-switch"
                  />
                </div>

                {/* Manual offer categories (chips in Ofertas page) */}
                {(() => {
                  let cats = [];
                  try { cats = JSON.parse(settings.offer_categories || '[]'); } catch (e) { cats = []; }
                  const manualCats = (Array.isArray(cats) ? cats : []).filter(c => c && c.id && (c.mode || 'manual') === 'manual');
                  if (manualCats.length === 0) {
                    return (
                      <p className="text-xs text-slate-400">
                        Aún no tienes categorías manuales. Créalas en Configuración → "Categorías de Ofertas".
                      </p>
                    );
                  }
                  const selected = formData.offer_categories || [];
                  const toggle = (id) => {
                    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
                    setFormData({ ...formData, offer_categories: next });
                  };
                  return (
                    <div className="space-y-2">
                      <Label>Categorías de la oferta</Label>
                      <div className="flex flex-wrap gap-2">
                        {manualCats.map((c) => {
                          const on = selected.includes(c.id);
                          return (
                            <button
                              type="button"
                              key={c.id}
                              data-testid={`offer-cat-toggle-${c.id}`}
                              onClick={() => toggle(c.id)}
                              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                            >
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-slate-400">Las categorías "Por precio" se asignan solas según el precio de oferta.</p>
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Código del Cupón *</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="DESCUENTO20"
                      className="font-mono uppercase"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Compra Mínima</Label>
                    <Input
                      type="number"
                      value={formData.min_purchase}
                      onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Stock / cantidad disponible (opcional)</Label>
                  <Input
                    type="number"
                    data-testid="coupon-stock-input"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="Vacío = ilimitado"
                    min="0"
                  />
                  <p className="text-xs text-slate-500">Cada canje resta 1. Al llegar a 0 el cupón se muestra en gris como &quot;Agotado&quot; (no se elimina). Déjalo vacío para stock ilimitado.</p>
                </div>
                <div className="space-y-2">
                  <Label>Link de redirección (opcional)</Label>
                  <Input
                    data-testid="coupon-affiliate-link-input"
                    value={formData.affiliate_link}
                    onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
                    placeholder="Vacío = usa el link por defecto de la configuración"
                  />
                  <p className="text-xs text-slate-500">A dónde se envía al usuario al presionar &quot;Copiar y Canjear&quot;. Si lo dejas vacío, se usa el link por defecto.</p>
                </div>
              </>
            )}

            {/* Expiration Type */}
            <div className="space-y-3">
              <Label>Tipo de Expiración</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setExpirationType('date')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    expirationType === 'date'
                      ? 'bg-royal text-white border-royal'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-royal'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Por Fecha
                </button>
                <button
                  type="button"
                  onClick={() => setExpirationType('time')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    expirationType === 'time'
                      ? 'bg-royal text-white border-royal'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-royal'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  Por Tiempo
                </button>
              </div>

              {expirationType === 'date' ? (
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                />
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Horas</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.expiration_hours}
                      onChange={(e) => setFormData({ ...formData, expiration_hours: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Minutos</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={formData.expiration_minutes}
                      onChange={(e) => setFormData({ ...formData, expiration_minutes: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="cursor-pointer">Cupón Activo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>

            {/* Custom Colors Section */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowCustomColors(!showCustomColors)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-royal" />
                  <span className="font-medium text-slate-700">Colores Personalizados</span>
                </div>
                {showCustomColors ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>

              {showCustomColors && (
                <div className="mt-3 space-y-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Deja vacío para usar los colores predeterminados</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'custom_bg_color', label: 'Fondo' },
                      { key: 'custom_discount_bg', label: 'Descuento' },
                      { key: 'custom_button_bg', label: 'Botón' },
                      { key: 'custom_button_text', label: 'Texto botón' },
                      { key: 'custom_text_color', label: 'Texto' },
                      { key: 'custom_min_purchase_bg', label: 'Compra mín.' },
                      { key: 'custom_border_color', label: 'Borde punteado' },
                    ].map(item => (
                      <div key={item.key} className="space-y-1">
                        <Label className="text-xs">{item.label}</Label>
                        <div className="flex gap-1">
                          <Input
                            type="color"
                            value={formData[item.key] || '#ffffff'}
                            onChange={(e) => setFormData({ ...formData, [item.key]: e.target.value })}
                            className="w-10 h-8 p-0.5"
                          />
                          <Input
                            value={formData[item.key]}
                            onChange={(e) => setFormData({ ...formData, [item.key]: e.target.value })}
                            placeholder="Auto"
                            className="flex-1 text-xs"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-canary text-slate-900 hover:bg-canary-hover"
            >
              {saving ? 'Guardando...' : editingCoupon ? 'Actualizar' : 'Crear Cupón'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCupones;
